<template>
    <div class="receipt-invoice-me invoice">

        <!-- Invoice Header -->
        <div
            v-if="venue.invoice_header"
            class="ck-content ck-custom"
            v-html="venue.invoice_header"
        />

        <!-- Header -->
        <div
            v-else
            class="receipt-header"
        >
            <p>{{ venue.company_name }}</p>
            <p>{{ venue.name }}</p>
            <p v-if="venue.tax_id_number && venue.tax_id_number !== 13">
                {{ $t('general.tax_id_number') + ': ' + venue.tax_id_number }}
            </p>
            <p>{{ venue.address }}</p>
        </div>

        <div class="receipt-subheader">

            <!-- Receipt Details -->
            <div v-if="!preview" class="receipt-details">
                <p v-if="order.internal_number">
                    {{ `${!order.isProforma() ? $t('invoice.number') : $t('invoice.proforma.number')}: ${order.internal_number}` }}
                </p>
                <p v-if="order.number && locale !== 'me'">{{ $t('general.receipt_number_serial') + ': ' + order.number }}</p>
                <p v-if="order.number && locale === 'me'">{{ $t('general.receipt_number') + ': ' + order.getFullNumber() }}</p>
                <p v-if="order.getUser()">{{ $t('general.operator') + ': ' + order.getUser().name + ' / ' + order.getUser().hash }}</p>
                <p v-if="order.getLastActivityAt()">{{ $t('general.time') + ': ' }} {{ order.getLastActivityAt() | dateTime }}</p>
                <p v-if="order.paymentType() && !order.isProforma()">{{ $t('general.paid_by') + ': ' + order.paymentType() }}</p>
                <p v-if="order.isSummary()">{{ $t('general.receipt_type') + ': ' + $t('general.receipt_summary') }}</p>
                <p v-if="order.isAdvance()">{{ $t('general.receipt_type') + ': ' + $t('general.advance') }}</p>
                <p v-if="order.due_date">{{ $t('general.due_date') }}: {{ order.due_date | date }}</p>
                <p v-if="order.tax_period">{{ $t('general.tax_period') }}: {{ order.tax_period }}</p>
            </div>

            <div v-if="preview" class="receipt-details">
                <p v-if="options.internalNumber">
                    {{ `${!options.isProforma ? $t('invoice.number') : $t('invoice.proforma.number')}: ${options.internalNumber}` }}
                </p>
                <p v-if="order.number && !options.isProforma">{{ $t('general.receipt_number_serial') + ': ' + order.number }}</p>
                <p v-if="order.getUser()">{{ $t('general.operator') + ': ' + order.getUser().name + ' / ' + order.getUser().hash }}</p>
                <p>{{ $t('general.time') + ': ' }} {{ moment() | dateTime }}</p>
                <p v-if="options.isSummary">{{ $t('general.receipt_type') + ': ' + $t('general.receipt_summary') }}</p>
                <p v-if="!options.isProforma">{{ $t('general.paid_by') + ': ' + $t('orders.payment_types.invoice') }}</p>
                <p v-if="order.due_date">{{ $t('general.due_date') }}: {{ order.due_date | date }}</p>
                <p v-if="order.tax_period">{{ $t('general.tax_period') }}: {{ order.tax_period }}</p>
            </div>

            <!-- Invoice Client Details -->
            <div v-if="client" class="receipt-buyer">
                <p><strong>{{ $tc('general.buyer', 1) }}</strong></p>
                <p v-if="client.name">{{ client.name }}</p>
                <p v-if="client.cost_center">
                    {{ $t('general.buyer_cost_center') + ': ' + client.cost_center }}
                </p>
                <p v-if="client.address">
                    {{ `${client.address}` }}
                    <template v-if="client.city">, {{ `${client.city}` }}</template>
                    <template v-if="client.country">, {{ `${client.country}` }}</template>
                </p>
                <p v-if="client.id_number">
                    {{ $t(`clients.id_types.${client.id_type}`) + ': ' + client.id_number }}
                </p>
                <p v-if="client.tax_id_number && client.tax_id_number !== client.id_number">
                    {{ $t('general.tax_id_number') + ': ' + client.tax_id_number }}
                </p>
                <p v-if="client.registration_number">
                    {{ $t('general.registration_number') + ': ' + client.registration_number }}
                </p>
                <p v-if="client.tax_id_card_number">
                    {{ $t('general.tax_id_card_number') + ': ' + client.tax_id_card_number }}
                </p>
                <p v-if="client.vat_number">
                    {{ $t('general.vat_number_short') + ': ' + client.vat_number }}
                </p>
            </div>

        </div>

        <div v-if="order.start_date && order.end_date" class="receipt-period">
            {{ $t('invoice.period') }}: {{ order.start_date | date }} - {{ order.end_date | date }}
        </div>

        <!-- Receipt Items -->
        <table class="receipt-items">
            <tbody>

                <!-- Heading Row -->
                <tr v-if="productItems.length" class="heading-row">
                    <td align="left">{{ $t('general.number_short') }}</td>
                    <td align="left">{{ $t('general.code') }}</td>
                    <td align="left" class="name">{{ $tc('general.item', 1) }}</td>
                    <td align="right">{{ $t('invoice_preview.quantity') }}</td>
                    <td align="right" width="70px">{{ $t('invoice_preview.unit_price_before_tax') }}</td>
                    <td align="right">{{ $t('invoice_preview.tax_rate') }}</td>
                    <td align="right" width="70px">{{ $t('invoice_preview.unit_price') }}</td>
                    <td align="right">{{ $t('invoice_preview.discount') }}</td>
                    <td align="right" width="90px">{{ $t('invoice_preview.unit_price_after_discount') }}</td>
                    <td align="right" width="90px">{{ $t('invoice_preview.price_before_tax') }}</td>
                    <td align="right" width="70px">{{ $t('invoice_preview.tax') }}</td>
                    <td align="right" width="80px">{{ $t('invoice_preview.to_pay') }}</td>
                </tr>

                <!-- Order Items -->
                <template v-for="(item, index) in productItems">
                    <tr
                        :key="`invoice-${index}`"
                        :class="{ 'border-bottom': (!isAllegra || !item.comments.length) && index != productItems.length - 1 }"
                    >
                        <td align="left">{{ index + 1 }}.</td>
                        <td align="left">
                            {{ item.getProduct() && item.getProduct().code ? item.getProduct().code : '' }}
                        </td>
                        <td align="left" class="name">{{ item.name }}</td>
                        <td align="right">
                            {{ item.getCount() }}
                            <small>{{ item.getProduct() ? item.getProduct().getUnitOfMeasureSold() : '' }}</small>
                        </td>
                        <td align="right">{{ item.getUnitPriceBeforeTax().toFormat('0.0000') }}</td>
                        <td align="right">{{ item.getTaxRate() }}%</td>
                        <td align="right">{{ item.getUnitPriceAfterTax().toFormat('0.00') }}</td>
                        <td align="right">{{ item.getDiscountPercent() }}%</td>
                        <td align="right">{{ item.getUnitPriceAfterDiscount().toFormat('0.00') }}</td>
                        <td align="right">{{ item.getPriceBeforeTax().toFormat('0.00') }}</td>
                        <td align="right">{{ item.getTax().toFormat('0.00') }}</td>
                        <td align="right">{{ item.getToPay().toFormat('0.00') }}</td>
                    </tr>

                    <tr
                        v-if="isAllegra && item.comments.length" :key="`invoice-item-comment-${index}`"
                        :class="{ 'border-bottom': index != productItems.length - 1 }"
                    >
                        <td></td>
                        <td></td>
                        <td colspan="10">
                            <span style="font-size: 11px;" v-html="nl2br(item.comments[0])"></span>
                        </td>
                    </tr>
                </template>

                <!-- Receipt Items for Summary Order -->
                <template v-if="receiptItems.length">

                    <!-- Heading Row -->
                    <tr class="heading-row">
                        <td :colspan="11">{{ $tc('general.receipt', 1) }}</td>
                        <td align="right">{{ $t('general.price') }}</td>
                    </tr>

                    <template v-for="(item, index) in receiptItems">

                        <!-- Product Row -->
                        <tr :key="`receipt-${index}`">
                            <td :colspan="11">{{ item.name }}</td>
                            <td align="right">{{ item.getPrice().toFormat('0.00') }}</td>
                        </tr>

                    </template>
                </template>

                <tr class="divider">
                    <td :colspan="12" />
                </tr>
            </tbody>
        </table>

        <div class="receipt-footer">
            <div class="footer-item additional-info">
                <p v-if="locale === 'me' && !order.isProforma() && !order.isExternal()">
                    <span v-if="order.correctsOrders().length" class="code">
                        REF IKOF: {{ order.correctsOrders()[0].getIic() }}
                    </span>
                    <span class="code">
                        IKOF: {{ order.getIic() }}
                    </span>
                    <span v-if="order.fiscal_code" class="code">
                        JIKR: {{ order.fiscal_code }}
                    </span>
                </p>

                <p v-if="order.getFlag('otaSync').length">
                    {{ $t('integrations.ota_sync.room_number') }}: {{ order.getFlag('otaSync')[0] }}
                </p>

                <p v-if="order.note" class="note">
                    {{ order.note }}
                </p>

                <p v-if="!venue.taxes_enabled" class="note">
                    {{ $t('general.note') }}: {{ $t('order.exempt_from_tax_comment') }}
                </p>

                <div class="signatures">
                    <div class="signature">
                        <div>___________________</div>
                        <div>{{ $tc('invoice.delivered_by') }}</div>
                    </div>

                    <div class="signature">
                        <div>___________________</div>
                        <div>{{ $tc('invoice.received_by') }}</div>
                    </div>
                </div>
            </div>

            <div class="footer-item summary">

                <template v-if="locale === 'me'" v-for="(tax, index) in taxes">
                    <div
                        v-if="!tax.id"
                        :key="index"
                        class="summary-item"
                    >
                        <p>{{ $t('order.exempt_from_tax') }} {{ tax.label.replace('VAT_','') }}:</p>
                        <p class="amount">{{ Money.parse(tax.price_before_tax).toFormat('0.00') }}</p>
                    </div>
                </template>

                <template v-for="(tax, index) in taxes">
                    <div
                        v-if="locale === 'me' && tax.id"
                        :key="'beforetax-' + index"
                        class="summary-item"
                    >
                        <p>{{ `${tax.label} ${$t('general.price_pre_tax').toLowerCase()}:` }}</p>
                        <p class="amount">{{ Money.parse(tax.price_before_tax).toFormat('0.00') }}</p>
                    </div>

                    <div
                        v-if="tax.id"
                        :key="index"
                        class="summary-item"
                    >
                        <p>{{ `${tax.label} (${tax.rate}%):` }}</p>
                        <p class="amount">{{ Money.parse(tax.value).toFormat('0.00') }}</p>
                    </div>
                </template>

                <div v-if="taxes.length" class="divider" style="margin: 5px 0"></div>

                <div v-if="summary.exemptFromTax && summary.exemptFromTax.toUnit() > 0" class="summary-item">
                    <p>{{ $t('invoice_preview.summary.exempt_from_tax') }}:</p>
                    <p class="amount">{{ summary.exemptFromTax.toFormat('0.00') }}</p>
                </div>

                <div v-if="summary.priceBeforeTax" class="summary-item">
                    <p>{{ $t('invoice_preview.summary.price_before_tax') }}:</p>
                    <p class="amount">{{ summary.priceBeforeTax.toFormat('0.00') }}</p>
                </div>

                <div class="summary-item">
                    <p>{{ $t('invoice_preview.summary.tax') }}:</p>
                    <p class="amount">{{ order.getTax().toFormat('0.00') }}</p>
                </div>

                <div class="divider" style="margin: 5px 0"></div>

                <div v-if="summary.price" class="summary-item">
                    <p>{{ $t('invoice_preview.summary.price_after_tax') }}:</p>
                    <p class="amount">{{ summary.price.toFormat('0.00') }}</p>
                </div>

                <div class="summary-item">
                    <p>{{ $t('invoice_preview.summary.discount') }}:</p>
                    <p class="amount">{{ summary.discountAmount.toFormat('0.00') }}</p>
                </div>

                <div class="divider" style="margin: 5px 0"></div>

                <div v-if="summary.toPay" class="summary-item to-pay">
                    <p>{{ $t('invoice_preview.summary.to_pay') }}:</p>
                    <p class="amount">{{ summary.toPay.toFormat() }}</p>
                </div>

                <div v-for="paymentType in paymentTypes" :key="paymentType.label" class="summary-item">
                    <p>{{ paymentType.display }}:</p>
                    <p class="amount">{{ paymentType.amount.toFormat() }}</p>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import CashRegister from '@/models/CashRegister'
import Client from '@/models/Client';
import OrderItem from '@/models/OrderItem';

export default {
    props: {
        order: {
            type: Object,
            default: () => null
        },
        options: {
            type: Object,
            default: () => {}
        },
        preview: {
            type: Boolean,
            default: false
        }
    },

    computed: {
        venue() {
            return this.options.venue || this.tenant?.venue
        },

        client() {
            return this.order.client || Client.find(this.order.client_id)
        },

        productItems() {
            if(this.order.isSummary()) {
                return this.order.paysForOrders()
                    ? OrderItem.merge(this.order.paysForOrders().flatMap(order => order.items), this.order)
                    : [];
            }

            return this.order.items
        },

        receiptItems() {
            return this.order.isSummary() ? this.order.items : []
        },

        taxes() {
            return this.order.taxes.filter(taxItem => !!taxItem.id || (!taxItem.id && taxItem.label.includes('VAT_')))
        },

        summary() {
            if(this.order.isSummary() && !this.order.paysForOrders()) {
                return { toPay: this.order.getToPay() }
            }

            return {
                exemptFromTax: this.order.getExemptFromTax(),
                priceBeforeTax: this.order.getPriceBeforeTax(),
                price: this.order.getPrice(),
                discountAmount: this.order.getDiscountAmount(),
                toPay: this.order.getToPay(),
            }
        },

        paymentTypes() {
            if(this.options.isProforma) return [];

            if(this.preview) {
                let paymentTypes = [];
                if(this.order.getPaidAdvance().toUnit() > 0) {
                    paymentTypes.push({
                        display: this.$t('orders.payment_types.advance'),
                        amount: this.order.getPaidAdvanceEffective()
                    });
                }

                if(this.order.getLeftToPay().toUnit() !== 0) {
                    paymentTypes.push({
                        display: this.$t('orders.payment_types.invoice'),
                        amount: this.order.getLeftToPay()
                    });
                }

                return paymentTypes.filter(type => type.amount.toUnit() != 0);
            }

            let allPaymentTypes = [];
            let paymentTypes = this.order.getPayments();
            const advancePayment = paymentTypes.find(p => p.value === 'advance')

            if(advancePayment) {
                allPaymentTypes.push(advancePayment)
                paymentTypes = paymentTypes.filter(p => p.value !== 'advance')
            }

            allPaymentTypes = [...allPaymentTypes, ...paymentTypes];

            return allPaymentTypes
        }
    },
};
</script>

<style lang="scss" scoped>
.receipt-invoice-me {
    font-family: Arial, Helvetica, sans-serif;

    &.invoice {
        font-size: 13px;
        color: black;
        -webkit-user-select: text;
        -webkit-app-region: none;

        p {
            padding: 2px 0 !important;
            margin: 0 !important;
        }

        .divider {
            border-top: 1px solid black;
            height: 1px !important;
        }

        .receipt-header {
            padding: 10px;
            text-align: center;
            font-size: 16px;
            font-weight: 500;
        }

        .receipt-subheader {
            width: 100%;
            padding: 10px;
            display: flex;
            flex-wrap: wrap-reverse;
            align-items: center;

            .receipt-details {
                flex: 1;
                min-width: 300px;
            }

            .receipt-buyer {
                border-radius: 4px;
                border: 1px solid black;
                width: 250px;
                padding: 10px;
                margin: 10px 10px 10px 0;
            }
        }

        .receipt-period {
            padding: 10px;
        }

        .receipt-items {
            border-collapse: collapse;
            width: 100%;

            tbody {

                tr {
                    vertical-align: top;

                    &.border-bottom {
                        border-bottom: 1px solid #b0b0b0;
                    }

                    td {
                        padding: 4px;

                        &:first-child {
                            padding-left: 10px;
                        }

                        &.name {
                            min-width: 130px;
                            max-width: 200px;
                            word-break: break-word;
                            white-space: normal;
                        }

                        &:last-child {
                            padding-right: 10px;
                        }
                    }

                    &:hover {
                        background: #f7f7f7;
                    }
                }

                .heading-row {
                    padding: 0 5px;
                    vertical-align: middle;
                    border-top: 1px solid black;
                    border-bottom: 1px solid black;
                    height: 40px;
                    font-size: 11px;
                    font-weight: 600;
                    background: #f1f1f1;
                }
            }
        }

        .receipt-footer {
            display: flex;
            flex-wrap: wrap-reverse;
            margin-bottom: 20px;
            break-inside: avoid;

            .footer-item {
                flex: 1;

                &.additional-info {
                    min-width: 300px;
                    padding: 10px 0 10px 10px;

                    .code {
                        display: block;
                    }

                    .note {
                        margin-top: 10px !important;
                        white-space: pre-line;
                        font-size: 12px;
                        max-width: 400px;
                        text-align: justify;
                    }

                    .signatures {
                        display: flex;
                        flex-wrap: wrap;

                        .signature {
                            margin-top: 20px;
                            text-align: center;

                            &:first-child {
                                margin-right: 20px;
                            }
                        }
                    }
                }

                &.qrcode {
                    max-width: 170px;
                    text-align: center;
                    padding: 10px;
                }

                &.summary {
                    padding: 10px 10px 10px 0;
                    min-width: 220px;
                    max-width: 250px;

                    .summary-item {
                        padding: 1px;
                        display: flex;
                        justify-content: space-between;

                        &.to-pay {
                            font-weight: 500;
                            font-size: 24px;
                        }

                        .amount {
                            text-align: right;
                        }
                    }
                }
            }
        }

    }
}
</style>
